import { useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { Flex, Portal, Text } from '@nula/core-ui';

import { useEntityAttendees } from '@/lib/recordings/use-entity-attendees';
import { displayNameFor } from '@/lib/recordings/attendee-display';
import { AvatarFor } from './attendee-card-content';
import type { AttendeeView } from '@/lib/recordings/types/attendees';
import styles from './speaker-hover-card.module.css';

// ── Types ───────────────────────────────────────────────────────────────────

export type SpeakerHoverCardProps = {
  /** The entity ID of the recording. Null disables the hover card. */
  entityId: string | null;
  /** The email of the speaker label. Null disables the hover card. */
  email: string | null;
  /** The trigger element — typically the readonly speaker label display surface. */
  children: ReactNode;
};

// Nudge the tooltip slightly down/right of the pointer so it doesn't sit
// directly under the cursor (which would cause flicker as the cursor enters
// the tooltip's own bounding box). Tooltip has pointer-events: none so this
// is a visual offset only.
const POINTER_OFFSET_X = 12;
const POINTER_OFFSET_Y = 16;

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Wraps a speaker label display surface with a mouse-anchored tooltip showing
 * compact company info: name + "Title at Company" subtitle + (optional) short
 * company description.
 *
 * Renders `children` directly (no hover behavior) when:
 * - `entityId` is null
 * - `email` is null
 * - the attendee is not found in the list
 * - the attendee's `enrichmentStatus` is not `'enriched'`
 *
 * Tooltip follows the pointer; hides on mouse leave. Click events on
 * `children` propagate normally so the existing EditableText click → edit
 * flow is unaffected.
 */
export function SpeakerHoverCard({ entityId, email, children }: SpeakerHoverCardProps) {
  const { attendees: data } = useEntityAttendees(entityId);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  // Look up the attendee by email — only when we have both identifiers.
  const attendee =
    email && data
      ? data.find((a) => a.email === email)
      : undefined;

  // Only show the hover card for enriched attendees.
  const isEnriched = attendee?.enrichmentStatus === 'enriched';

  // Passthrough: no hover behavior when there's nothing useful to show.
  if (!entityId || !email || !attendee || !isEnriched) {
    return <>{children}</>;
  }

  const name = displayNameFor(attendee);

  const handleMouseEnter = (e: MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseMove = (e: MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseLeave = () => {
    setPos(null);
  };

  return (
    <>
      <span
        className={styles.triggerWrap}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {pos ? (
        <Portal>
          <div
            data-testid="speaker-hover-tooltip"
            role="tooltip"
            className={styles.tooltip}
            style={{
              '--tooltip-x': `${pos.x + POINTER_OFFSET_X}px`,
              '--tooltip-y': `${pos.y + POINTER_OFFSET_Y}px`,
            } as CSSProperties}
          >
            <CompactCardBody attendee={attendee} name={name} />
          </div>
        </Portal>
      ) : null}
    </>
  );
}

// ── Compact card body ────────────────────────────────────────────────────────

/**
 * Two-column layout: small avatar (left) + name/subtitle stack (right).
 * No description, no LinkedIn, no email, no location — those live in the
 * side panel's full card. Subtitle is shown only when title and/or company
 * are present.
 */
function CompactCardBody({ attendee, name }: { attendee: AttendeeView; name: string }) {
  return (
    <Flex align="center" gap="sm">
      <AvatarFor attendee={attendee} name={name} size={28} />
      <Flex vertical className={styles.textStack}>
        <Text className={styles.name}>{name}</Text>
        {attendee.title && attendee.companyName ? (
          <Text className={styles.subtitle}>
            {attendee.title} at{' '}
            <Text className={styles.companyName}>{attendee.companyName}</Text>
          </Text>
        ) : attendee.title ? (
          <Text className={styles.subtitle}>{attendee.title}</Text>
        ) : attendee.companyName ? (
          <Text className={styles.subtitle}>
            <Text className={styles.companyName}>{attendee.companyName}</Text>
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
}
